"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GET_MEETING_PREP, getMeetingPrep } from "@/api";
import { MeetingPrepStatus, type MeetingPrepEntity } from "@/types";
import { useMeetingPrepWebSocketSubscriptions } from "@/components/pages/MeetingPrep/MeetingPrepDetails/useMeetingPrepWebSocketSubscriptions";
import { usePollingGate } from "./usePollingGate";
import { useReconnectRefetch } from "./useReconnectRefetch";

const GENERATING_PREP_POLL_INTERVAL_MS = 5_000;

export const isMeetingPrepGenerating = (
  prep?: Pick<MeetingPrepEntity, "status"> | null,
) => prep?.status === MeetingPrepStatus.Generating;

/**
 * `refetchInterval` for a prep query. The meeting-prep websocket event ends a
 * generation; this polls only while that prep is generating and the socket is
 * down, as the conversation query does.
 */
export const useGeneratingPrepRefetchInterval = () => {
  const pollInterval = usePollingGate(GENERATING_PREP_POLL_INTERVAL_MS);
  return useCallback(
    (query: { state: { data?: MeetingPrepEntity | null } }) =>
      isMeetingPrepGenerating(query.state.data) ? pollInterval : false,
    [pollInterval],
  );
};

/** Catches up on prep events missed while the socket was down. */
export const useMeetingPrepReconnectRefetch = (meetingPrepId?: string) => {
  const queryClient = useQueryClient();
  useReconnectRefetch(() => {
    if (meetingPrepId) {
      queryClient.invalidateQueries({
        queryKey: [GET_MEETING_PREP, meetingPrepId],
      });
    }
  });
};

// "settled" covers READY and FAILED: both open on the details page, which
// offers a retry for a failed prep. "gone" is a prep that no longer exists.
export type MeetingPrepGenerationState =
  "loading" | "generating" | "settled" | "gone";

/** Follows a prep generated in the background until it settles. */
export const useMeetingPrepGeneration = (
  meetingPrepId?: string,
): MeetingPrepGenerationState => {
  const queryClient = useQueryClient();
  const enabled = !!meetingPrepId;
  const { data, status } = useQuery({
    queryKey: [GET_MEETING_PREP, meetingPrepId],
    queryFn: () => getMeetingPrep(meetingPrepId),
    enabled,
    // A failed check must not end the wait: the outcome event may already
    // have passed, so retry with React Query's capped backoff until it lands.
    // A missing prep is a successful response and is never retried.
    retry: true,
    refetchInterval: useGeneratingPrepRefetchInterval(),
  });

  useMeetingPrepWebSocketSubscriptions({
    meetingPrepId: meetingPrepId ?? "",
    enabled,
    queryClient,
  });
  useMeetingPrepReconnectRefetch(meetingPrepId);

  if (!enabled || status !== "success") return "loading";
  if (isMeetingPrepGenerating(data)) return "generating";
  return data ? "settled" : "gone";
};
